import type { AutoRefreshScheduleType } from 'components/Reports/reports.types';

/** Sunday first, matching `Date.getDay()` and the COMS `days_of_week` values. */
export const WEEKDAYS = [
  { value: 0, short: 'Sun', long: 'Sunday' },
  { value: 1, short: 'Mon', long: 'Monday' },
  { value: 2, short: 'Tue', long: 'Tuesday' },
  { value: 3, short: 'Wed', long: 'Wednesday' },
  { value: 4, short: 'Thu', long: 'Thursday' },
  { value: 5, short: 'Fri', long: 'Friday' },
  { value: 6, short: 'Sat', long: 'Saturday' },
] as const;

export const DEFAULT_TIME_OF_DAY = '07:00';

const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/;

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function parseScheduleType(value: unknown): AutoRefreshScheduleType {
  return value === 'daily' || value === 'weekly' ? value : 'interval';
}

/** Whole weekdays 0–6, deduplicated and sorted. */
export function sortedDays(days: readonly number[]): number[] {
  return [...new Set(days)]
    .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)
    .sort((a, b) => a - b);
}

/**
 * Why a daily or weekly schedule can't be saved, or null when it can. Interval
 * schedules are validated separately.
 */
export function validateSchedule(params: {
  scheduleType: AutoRefreshScheduleType;
  timeOfDay: string;
  daysOfWeek: readonly number[];
}): string | null {
  if (params.scheduleType === 'interval') return null;
  if (!TIME_OF_DAY.test(params.timeOfDay)) return 'Choose a time of day.';
  if (
    params.scheduleType === 'weekly' &&
    sortedDays(params.daysOfWeek).length === 0
  ) {
    return 'Choose at least one day of the week.';
  }
  return null;
}

/** "Daily at 07:00 (America/Chicago)" or "Mon, Thu at 18:30 (UTC)". */
export function describeSchedule(params: {
  scheduleType: AutoRefreshScheduleType;
  timeOfDay: string;
  daysOfWeek: readonly number[];
  timezone: string;
}): string {
  const zone = params.timezone ? ` (${params.timezone})` : '';
  if (params.scheduleType === 'daily')
    return `Daily at ${params.timeOfDay}${zone}`;
  const days = sortedDays(params.daysOfWeek);
  if (days.length === 7) return `Daily at ${params.timeOfDay}${zone}`;
  const labels = days.map((day) => WEEKDAYS[day].short).join(', ');
  return `${labels || 'No days'} at ${params.timeOfDay}${zone}`;
}
